import { Button, Progress, Tag, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import {
  PlannerListItem,
  RestrictionType,
} from "api/resources/learningItems/types"
import clsx from "clsx"
import { getRestrictionIcon } from "features/AccessManagement/CheckRestrictions"
import { useCpat } from "features/Cpat/useCpat"
import {
  getDeliveryModeBadge,
  getParentItem,
  getProductImage,
} from "features/LearningItems/getProductDisplayConfig"
import { getPercentage } from "features/Library/CourseInfoAndProgress"
import { context } from "global/Context/context"
import { useContext, useState } from "react"
import { Link } from "react-router-dom"
import routes from "routes"
import { learningItemName, learningItemSlugs } from "utils/constants"
import { generateModuleAccessRequestLink } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"
import { PropsWithClassName } from "utils/types"

import BeforeYouStart from "../SuraasaPlusNewYearOnboarding/ImpactCourseTour/BeforeYouStart"

const ResumeLearning = ({ className }: PropsWithClassName) => {
  const { data: lastAccessedItem, isError } = useQuery({
    queryFn: () => api.learningItems.getLastAccessedItem(),
    queryKey: ["last-accessed-item"],
  })

  if (isError || !lastAccessedItem) return null

  return (
    <div className={className}>
      <ResumeLearningCard
        progress={lastAccessedItem.progress}
        item={lastAccessedItem}
      />
    </div>
  )
}

type Props = PropsWithClassName<{
  href?: string
  item: Pick<PlannerListItem, "name" | "slug" | "learningItemType" | "image">
  progress: PlannerListItem["progress"] | null
}>

export const ResumeLearningCard = ({
  className,
  progress,
  item,
  href,
}: Props) => {
  const { learningRestrictions } = useContext(context)
  const { enrollments, pgctl } = useEnrollments()

  const enrollment = enrollments?.find(x => x.slug === item.slug)

  const completedItems = progress?.completedUserPlannerItems ?? 0
  const totalItems = progress?.totalUserPlannerItems ?? 0

  const percentage = getPercentage({
    total: totalItems,
    completed: completedItems,
  })

  const isCompleted = percentage === 100
  const isInProgress = !isCompleted && completedItems > 0

  const getLink = () => {
    if (href) return href

    const defaultLink = routes.parentItem
      .replace(":learningItemType", item.learningItemType)
      .replace(":slug", item.slug)

    if (!enrollment) return defaultLink

    if (enrollment?.parents.length) {
      const parent = enrollment.parents[0]

      return routes.parentItemCourse
        .replace(":learningItemType", parent.type)
        .replace(":parentSlug", parent.slug)
        .replace(":slug", item.slug)
    }

    return defaultLink
  }

  const isThroughPgCTL = getParentItem(enrollments, item.slug)?.isPgCTL === true
  const isThroughMed = getParentItem(enrollments, item.slug)?.isMED === true
  const isPGCTL = item.slug === learningItemSlugs.pgctl

  const badge = isPGCTL
    ? getDeliveryModeBadge({ deliveryMode: pgctl?.deliveryMode })
    : null

  const isImpactCourse = item.slug === learningItemSlugs.impactCourse
  const { hasCompletedCpat } = useCpat()

  const restrictionType = learningRestrictions?.find(
    restriction => restriction.learningItem.slug === item.slug
  )?.restrictionType
  const isRestricted = !!restrictionType

  const [attemptCPATFirstDialogOpen, setAttemptCPATFirstDialogOpen] =
    useState(false)

  const Root =
    isImpactCourse && !hasCompletedCpat ? "div" : isRestricted ? "div" : Link

  return (
    <>
      {isImpactCourse && (
        <BeforeYouStart
          open={attemptCPATFirstDialogOpen}
          handleClose={() => setAttemptCPATFirstDialogOpen(false)}
        />
      )}
      <Root
        className="text-start"
        to={getLink()}
        onClick={() => {
          if (isRestricted) return
          if (isImpactCourse && !hasCompletedCpat)
            setAttemptCPATFirstDialogOpen(true)
        }}
      >
        <div
          className={clsx(
            "overflow-hidden rounded-2xl border border-onSurface-200",
            className
          )}
        >
          <div className="p-2.5">
            <div className="mb-4 flex justify-between gap-3 sm:gap-0">
              <div className="flex max-w-[340px] flex-col">
                <Typography className="mb-1.5 text-preTitle !tracking-normal text-muted">
                  {isInProgress && (
                    <>
                      CONTINUE <span className="text-onSurface-300">• </span>
                    </>
                  )}
                  <span className="uppercase">{item.learningItemType}</span>
                  {isCompleted && " COMPLETED 🎉"}
                </Typography>
                <Typography variant="title3" className="text-black">
                  {item.name}
                </Typography>

                {isThroughPgCTL && (
                  <Typography
                    variant="strongSmallBody"
                    className="mt-1 text-muted"
                  >
                    {learningItemName.pgctl}
                  </Typography>
                )}
                {isThroughMed && (
                  <Typography
                    variant="strongSmallBody"
                    className="mt-1 text-muted"
                  >
                    {learningItemName.med}
                  </Typography>
                )}

                {badge && (
                  <Tag
                    className={clsx(
                      `mt-1.5 p-0.5 text-preTitle`,
                      badge.bg,
                      badge.color
                    )}
                  >
                    {badge.text}
                  </Tag>
                )}
              </div>
              <div className="relative size-[73px] shrink-0 overflow-hidden rounded-xl object-cover">
                {restrictionType && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-onSurface-800/50 text-white">
                    <div className="flex size-5 items-center justify-center rounded-lg bg-onSurface-900/80">
                      {getRestrictionIcon(restrictionType)}
                    </div>
                  </div>
                )}
                <img
                  src={getProductImage({
                    defaultImage: item.image,
                    slug: item.slug,
                    deliveryMode: pgctl.deliveryMode,
                  })}
                  alt=""
                  className="size-full rounded-xl border border-surface-100 object-cover"
                />
              </div>
            </div>
            <div className="flex flex-col items-start justify-between gap-2 sm:flex-row sm:gap-0">
              {restrictionType === RestrictionType.REVOKED ? (
                <Link
                  to={generateModuleAccessRequestLink({
                    modules: [item.name],
                    type: restrictionType,
                  })}
                >
                  <Button
                    color={null}
                    className="w-full border border-onSurface-300 text-onSurface-700 sm:w-fit"
                  >
                    Contact Us
                  </Button>
                </Link>
              ) : (
                <Button
                  color={isRestricted ? null : "black"}
                  className={clsx(
                    "rounded-[10px] font-semibold",
                    isRestricted &&
                      "w-full border border-onSurface-300 text-onSurface-700 sm:w-fit"
                  )}
                  disabled={isRestricted}
                >
                  {isInProgress || isCompleted
                    ? "Continue Learning"
                    : "Start Learning"}
                </Button>
              )}

              <div className="text-start sm:text-end">
                <Typography className="mb-0.5 text-strongSmallBody text-onSurface-800 sm:mb-1">
                  {percentage}%{" "}
                  <span className="text-smallBody text-muted">Complete</span>
                </Typography>
                <Progress
                  value={percentage}
                  className={clsx("w-15", {
                    "bg-onSurface-100": restrictionType,
                  })}
                  indicatorClassName={clsx({
                    "!bg-onSurface-500": restrictionType,
                    "bg-success-500": isCompleted,
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </Root>
    </>
  )
}

{
  /* <div className="flex flex-col-reverse items-start justify-between gap-2 sm:flex-row sm:gap-0">
            <Button
              color={null}
              className="border-gray-300 text-gray-700"
              variant="outlined"
            >
              View Gradebook
            </Button>
            <div className="flex gap-1">
              <Button
                color={null}
                className="!block !h-fit !bg-success-600 !px-1.5 !py-1 text-preTitle"
              >
                GPA <span className="ml-[15px] text-subtitle2">9.3</span>
              </Button>
              <Button
                color={null}
                className="!block !h-fit bg-onSurface-100 !px-1.5 !py-1 text-preTitle text-muted"
              >
                CREDITS EARNED{" "}
                <span className="ml-[15px] text-subtitle2 text-onSurface-900">
                  92
                </span>
              </Button>
            </div>
          </div> */
}

{
  /* {learningCompleted && (
            <>
              <Divider className="bg-onSurface-200" />
              <div
                className={clsx("rounded-b-2xl p-2.5", {
                  "bg-interactive-50": !isCertificateIssued,
                  "bg-success-50": isCertificateIssued,
                })}
              >
                {isCertificateIssued ? (
                  <>
                    <Typography
                      variant="strong"
                      className="mb-0.5 text-success-800"
                    >
                      Your Certificate is Being Processed
                    </Typography>
                    <Typography
                      variant="smallBody"
                      className="mb-1.5 text-muted"
                    >
                      Our team is performing a final review of your completion
                      details to ensure everything is in order. We&apos;ll
                      notify you once your certificate is approved and ready.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="strong"
                      className="mb-0.5 text-onSurface-900"
                    >
                      Get Your Certificate
                    </Typography>
                    <Typography
                      variant="smallBody"
                      className="mb-1.5 text-muted"
                    >
                      To ensure your certificate includes all the correct
                      details, please complete one final step by filling out the
                      form below.
                    </Typography>
                    <Link to="#">
                      <Typography className="inline-flex items-center gap-0.25 text-primary-500">
                        Fill Out the Form{" "}
                        <ArrowRight height={15} width={15} strokeWidth={2.5} />
                      </Typography>
                    </Link>{" "}
                  </>
                )}
              </div>
            </>
          )} */
}

// const MileStoneThree = () => {
//   return (
//     <div className="flex justify-between px-2.5 pt-2.5">
//       <div className="flex flex-col items-start">
//         <Typography
//           variant="preTitle"
//           className="mb-1.5 !tracking-wide text-muted"
//         >
//           Continue Your Journey
//         </Typography>
//         <Typography className="text-muted" variant="title3">
//           MileStone 2
//         </Typography>
//         <Typography
//           className="mb-6 max-w-[335px] text-onSurface-800"
//           variant="title3"
//         >
//           Communication and Language Program
//         </Typography>
//         <div className="flex items-start justify-start gap-1.5">
//           <Button color="black">Get In Touch</Button>
//           <Button
//             color={null}
//             variant="outlined"
//             className="mb-2.5 text-gray-700"
//           >
//             Learn More
//           </Button>
//         </div>
//       </div>
//       <div className="hidden h-full w-[268px] rounded-[13px] object-cover sm:block">
//         <img src={mileStoneThreeImg} alt="" />
//       </div>
//     </div>
//   )
// }

// if (viewCertificate)
//   return (
//     <div className="mb-4 flex justify-between overflow-hidden rounded-2xl border bg-onSurface-900">
//       <div className="flex flex-col items-start p-2.5">
//         <Typography
//           variant="preTitle"
//           className="mb-1.5 !tracking-wide text-warning-300"
//         >
//           CERTIFICATE
//         </Typography>
//         <Typography
//           className="mb-4 max-w-[254px] text-onSurface-50"
//           variant="title3"
//         >
//           Professional Graduate Certificate in Teaching & Learning
//         </Typography>
//         <Button
//           color={null}
//           className="mb-2.5 bg-onSurface-700 text-onSurface-50"
//         >
//           View Certificate
//         </Button>
//       </div>
//       <div className="hidden h-fit w-[315px] object-cover sm:block">
//         <img src={CertificateImg} alt="" />
//       </div>
//     </div>
//   )

export default ResumeLearning
